import type { DeliveryTimeFragment } from '@generated/graphql';
import { LeitungCalculator } from '@igus/echain-shared-articles';
import type { LeitungBeschriftung } from '@igus/echain-shared-articles';
import type { LengthJson } from '@igus/echain-shared-quantities';
import { Length, LengthUnits } from '@igus/echain-shared-quantities';
import type { ConfigurationFileContent } from '@igus/kopla-domain';
import { AssociationType } from '@libs/associations';
import type {
  ProductAttribute,
  ProductAttributeValueAmountUnit,
  ProductFragmentAttributes,
} from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import { getArticleDetail } from '@libs/catalog-service';
import { QUERY_KEYS } from '@libs/constants';
import {
  calculateTotalProductPrice,
  getAssociationProductDiscountPrice,
  getPriceDisplayTypeForOrderType,
  getProductBasePrice,
  getProductLengthPrice,
  mapArticleDetailResponseToPrices,
} from '@libs/product-price-utils';
import type {
  AssociationProductFragmentEnhanced,
  ModelAssociationProductFragmentEnhanced,
  PriceFragmentEnhanced,
  ProductFragmentEnhanced,
} from '@models/enhanced-fragments';
import * as Sentry from '@sentry/nextjs';
import produce from 'immer';
import { QueryClient } from 'react-query';
import type { GetState, SetState } from 'zustand';
import type { EchainProductStore, EchainProductStoreInitialData } from './useEchainProductStore';
import { getProductIdForLocale } from '@libs/product.helper';
import { ArticleType } from '@igus/echain-product-models';

// This type is a subset of an AssociationProductFragment, only containing the bare minimum of properties.
// It is only used for adding products to the partslist, that do not exist in our data (will mainly be used by the ia configurator)
export type AssociationDummyProductFragment = {
  id: string;
  id_us?: string;
  attributes: ProductFragmentAttributes;
};

// type guard to check if the product in the PartsListItem is supported by us (not a dummy fragment without price information for example)
export const isSupportedAssociationFragment = (
  fragment:
    | AssociationProductFragmentEnhanced
    | ModelAssociationProductFragmentEnhanced
    | AssociationDummyProductFragment
): fragment is AssociationProductFragmentEnhanced | ModelAssociationProductFragmentEnhanced =>
  (fragment as AssociationProductFragmentEnhanced | ModelAssociationProductFragmentEnhanced).prices !== undefined;

export type EchainAssociationType =
  | AssociationType.MOUNTING_BRACKETS
  | AssociationType.SHELVES
  | AssociationType.SEPARATORS
  | AssociationType.SUPPORT_TRAY
  | AssociationType.CABLES;

export enum EchainPartsListItemOrigin {
  SHOP = 'shop',
  CONFIGURATOR = 'configurator',
}

export type EchainPartsListItem = (
  | AssociationProductFragmentEnhanced
  | ModelAssociationProductFragmentEnhanced
  | AssociationDummyProductFragment
) & {
  quantity: number;
  totalPrice: number;
  associationType?: EchainAssociationType;
  currency?: string;
  prices?: PriceFragmentEnhanced[];
  delivery_times?: DeliveryTimeFragment[];
  cableLength?: Length;
  cableSteckerTyp?: string;
  cableUeberstandFestpunkt?: LengthJson;
  cableUeberstandMitnehmer?: LengthJson;
  cableBeschriftung?: LeitungBeschriftung;
  cableBeschriftungText?: string;
  origin: EchainPartsListItemOrigin;
  unknownProduct: boolean;
};

export type InteriorSeparationConfiguration = {
  configJson?: string;
  configId?: string;
  files?: InteriorSeparationConfigurationFiles;
};

export type InteriorSeparationConfigurationFiles = {
  config: ConfigurationFileContent;
  pdf: string;
  excel: string;
};

export type InteriorConfigurationItemPriceData = {
  id: string;
  price: number;
  currency: string;
};

export type PricesAndOrderTypeDataMapped = {
  [key: string]: {
    priceData: InteriorConfigurationItemPriceData;
    prices: PriceFragmentEnhanced[];
    orderType: number;
  };
};

export type EchainPartsList = {
  partsList: EchainPartsListItem[];
  configurationId?: string;
  iaConfig?: InteriorSeparationConfiguration;
  windowRef?: Window | null;
};

export interface EchainPartsListSliceData {
  eChainPartsList: EchainPartsList | null;
  loadDynamicPrices: boolean;
}

export interface EchainPartsListSlice extends EchainPartsListSliceData {
  setLoadDynamicPrices: (loadDynamicPrices: boolean) => void;
  getEchainPartsListItem: (partsListItemId: string) => EchainPartsListItem | undefined;
  addEchainPartsListItem: (
    item: AssociationProductFragmentEnhanced | ModelAssociationProductFragmentEnhanced,
    locale: string,
    associationType: EchainAssociationType,
    quantity?: number
  ) => Promise<void>;
  removeEchainPartsListItem: (echainPartsListItemId: string) => void;
  removeEchainPartsListItemsByAssociationTypes: (associationTypes: AssociationType[]) => void;
  setEchainPartsListItemQuantity: (partsListItemId: string, quantity?: number) => void;
  setEchainPartsListConfigurationId: (configurationId: string) => void;
  setIaConfig: (iaConfig: InteriorSeparationConfiguration, iaConfigParts: EchainPartsListItem[]) => void;
  updateIaConfigItemPricesAndOrderType: (pricesAndOrderTypeMap: PricesAndOrderTypeDataMapped) => void;
  updateCablePrices: () => void;
  removeIaConfig: () => void;
  setIaConfiguratorWindow: (windowRef: Window | null) => void;
  getIaConfiguratorWindow: () => { windowRef: Window | null };
  clear: () => void;
}

export const createEchainPartsListSlice = (
  set: SetState<EchainProductStore>,
  get: GetState<EchainProductStore>,
  initialData?: EchainProductStoreInitialData
) =>
  ({
    loadDynamicPrices: !!initialData?.loadDynamicPrices,
    eChainPartsList: initialData?.eChainPartsList ?? null,
    setLoadDynamicPrices: loadDynamicPrices => set(() => ({ loadDynamicPrices })),
    getEchainPartsListItem: partsListItemId =>
      get().eChainPartsList?.partsList.find(partsListItem => partsListItem.id === partsListItemId),
    addEchainPartsListItem: async (partsListItem, locale, associationType, quantity) => {
      let totalPrice = getAssociationProductDiscountPrice(partsListItem.prices, quantity || 1);

      const partsListItemProductIdForPriceRequest = getProductIdForLocale(partsListItem, locale);

      if (get().loadDynamicPrices) {
        const queryClient = new QueryClient();
        const articleDetailResult = await queryClient.fetchQuery([QUERY_KEYS.ARTICLE_DETAIL, partsListItem.id], () =>
          getArticleDetail({ articleNumbers: [partsListItemProductIdForPriceRequest], textMode: false })
        );

        const partsListItemOrderType = getAttributeValue<string>(
          partsListItem.attributes?.order_type as ProductAttribute<string>
        );

        const partItemPrices = mapArticleDetailResponseToPrices(
          articleDetailResult,
          getPriceDisplayTypeForOrderType(partsListItemOrderType),
          partsListItem.aggregated_product_prices[0]?.country_code
        );

        // dynamic prices can result in a currency change!
        partsListItem.prices.forEach((_, idx) => {
          // eslint-disable-next-line no-param-reassign
          partsListItem.prices[idx].currency_code = articleDetailResult.currency;
        });

        partsListItem.aggregated_product_prices.forEach((_, idx) => {
          // eslint-disable-next-line no-param-reassign
          partsListItem.aggregated_product_prices[idx].prices.currency = articleDetailResult.currency;
        });

        totalPrice = getAssociationProductDiscountPrice(partItemPrices[0].quantityDiscounts, quantity || 1);
      }

      set((state): EchainProductStore => {
        const currentEchainPartsList = state.eChainPartsList;
        // do not add an already existing item to the parts list
        if (currentEchainPartsList && currentEchainPartsList[partsListItem.id as keyof EchainPartsList]) {
          return { ...state };
        }

        const newPartList: EchainPartsListItem[] = [];

        // Add parts from current part list first.
        if (currentEchainPartsList) {
          // If the new item is of association type "mounting brackets" or "support tray" and the current list already
          // contains an item of those types, the existing item needs to be removed from the list.
          const filteredPartsList = [AssociationType.MOUNTING_BRACKETS, AssociationType.SUPPORT_TRAY].includes(
            associationType as AssociationType
          )
            ? currentEchainPartsList.partsList.filter(item => item.associationType !== associationType)
            : currentEchainPartsList.partsList;

          newPartList.push(...filteredPartsList);
        }

        newPartList.push({
          ...partsListItem,
          quantity: quantity || 1,
          totalPrice: totalPrice * (quantity || 1),
          associationType,
          unknownProduct: false,
          // parts coming in from the ia-configurator (origin = 'configurator') are added using setIaConfig and cannot
          // be set with addEchainPartsListItem cause they always have to be set together with an ia-configuration
          origin: EchainPartsListItemOrigin.SHOP,
        });

        return {
          ...state,
          eChainPartsList: {
            ...currentEchainPartsList,
            partsList: newPartList,
          },
        };
      });
    },
    setIaConfig: (iaConfig: InteriorSeparationConfiguration, iaConfigParts: EchainPartsListItem[]) => {
      set((state): EchainProductStore => {
        let partsList: EchainPartsListItem[] = [];

        // first remove all iaConfigurator parts from partslist
        if (state.eChainPartsList?.partsList) {
          partsList = state.eChainPartsList.partsList.filter(item => item.origin === EchainPartsListItemOrigin.SHOP);
        }
        // now add the new iaConfigurator parts
        iaConfigParts.forEach(item => partsList.push(item));

        if (state.eChainPartsList) {
          return {
            ...state,
            eChainPartsList: {
              ...state.eChainPartsList,
              partsList,
              iaConfig: {
                ...state.eChainPartsList?.iaConfig,
                ...iaConfig,
              },
            },
          };
        }
        return state;
      });
    },
    removeIaConfig: () => {
      set((state): EchainProductStore => {
        if (state.eChainPartsList) {
          return {
            ...state,
            eChainPartsList: {
              ...state.eChainPartsList,
              // remove all iaConfigurator parts from the partsList
              partsList: state.eChainPartsList.partsList.filter(item => item.origin === EchainPartsListItemOrigin.SHOP),
              iaConfig: undefined,
            },
          };
        }
        return state;
      });
    },
    removeEchainPartsListItem: echainPartsListItemId => {
      set((state): EchainProductStore => {
        if (state.eChainPartsList) {
          return {
            ...state,
            eChainPartsList: {
              ...state.eChainPartsList,
              partsList: [
                // iaConfigurator parts can only be deleted when the complete configuration is deleted
                ...state.eChainPartsList.partsList.filter(
                  partsListItem =>
                    (partsListItem.id !== echainPartsListItemId &&
                      partsListItem.origin === EchainPartsListItemOrigin.SHOP) ||
                    partsListItem.origin === EchainPartsListItemOrigin.CONFIGURATOR
                ),
              ],
            },
          };
        }
        return { ...state };
      });
    },

    removeEchainPartsListItemsByAssociationTypes: associationTypes => {
      set((state): EchainProductStore => {
        if (state.eChainPartsList) {
          const partsListWithoutGivenAssociationTypes = [
            ...state.eChainPartsList.partsList.filter(
              partsListItem =>
                ((!partsListItem.associationType ||
                  !associationTypes.includes(partsListItem.associationType as AssociationType)) &&
                  partsListItem.origin === EchainPartsListItemOrigin.SHOP) ||
                // iaConfigurator parts can only be deleted when the complete configuration is deleted
                partsListItem.origin === EchainPartsListItemOrigin.CONFIGURATOR
            ),
          ];

          return {
            ...state,
            eChainPartsList: {
              ...state.eChainPartsList,
              partsList: partsListWithoutGivenAssociationTypes,
            },
          };
        }

        return { ...state };
      });
    },

    setEchainPartsListItemQuantity: (partsListItemId, quantity = 1) => {
      set((state): EchainProductStore => {
        const currentEchainPartsList = state.eChainPartsList;
        if (currentEchainPartsList && currentEchainPartsList?.partsList) {
          return {
            ...state,
            eChainPartsList: {
              ...currentEchainPartsList,
              partsList: currentEchainPartsList?.partsList.map(partsListItem => {
                if (partsListItem.id === partsListItemId) {
                  const discountPrice = isSupportedAssociationFragment(partsListItem)
                    ? getAssociationProductDiscountPrice(partsListItem.prices, quantity || 1)
                    : undefined;
                  return {
                    ...partsListItem,
                    quantity,
                    totalPrice: discountPrice && discountPrice >= 0 ? discountPrice * quantity : discountPrice,
                  } as EchainPartsListItem;
                }
                return partsListItem;
              }),
            },
          };
        }
        return { ...state };
      });
    },
    setEchainPartsListConfigurationId: newConfigurationId => {
      set((state): EchainProductStore => {
        const currentEchainPartsList = state.eChainPartsList;
        if (currentEchainPartsList && currentEchainPartsList?.partsList) {
          return {
            ...state,
            eChainPartsList: {
              ...currentEchainPartsList,
              configurationId: newConfigurationId,
            },
          };
        }
        return { ...state };
      });
    },
    setIaConfiguratorWindow: (windowRef: Window | null) => {
      set((state): EchainProductStore => {
        if (state.eChainPartsList) {
          return {
            ...state,
            eChainPartsList: {
              ...state.eChainPartsList,
              windowRef,
            },
          };
        }
        return { ...state };
      });
    },
    getIaConfiguratorWindow: () => ({
      windowRef: get().eChainPartsList?.windowRef,
    }),
    clear: () => set(state => ({ ...state, eChainPartsList: null })),
    updateIaConfigItemPricesAndOrderType: pricesAndOrderTypeMap => {
      set((state): EchainProductStore => {
        const currentEchainPartsList = state.eChainPartsList;
        if (currentEchainPartsList && currentEchainPartsList.iaConfig) {
          return {
            ...state,
            eChainPartsList: {
              ...currentEchainPartsList,
              partsList: currentEchainPartsList.partsList?.map(currentItem => {
                // we only want to handle iaConfigurator parts here
                if (currentItem.origin === EchainPartsListItemOrigin.CONFIGURATOR) {
                  const { priceData, orderType, prices } = pricesAndOrderTypeMap[currentItem.id] || {};
                  if (priceData && orderType && prices && currentItem.unknownProduct) {
                    const priceType = getPriceDisplayTypeForOrderType(orderType?.toString());
                    const { totalCentPrice } = calculateTotalProductPrice(
                      priceType,
                      1,
                      currentItem.quantity,
                      priceData.price * 100,
                      0,
                      currentItem.prices
                    );
                    return {
                      ...currentItem,
                      attributes: { ...currentItem.attributes, order_type: `${orderType}` as ProductAttribute<string> },
                      prices,
                      totalPrice: totalCentPrice ? totalCentPrice / 100 : -1,
                      currency: priceData.currency,
                    } as EchainPartsListItem;
                  }
                }
                return currentItem;
              }),
              iaConfig: {
                ...currentEchainPartsList.iaConfig,
              },
            },
          };
        }
        return { ...state };
      });
    },
    updateCablePrices: () => {
      set(
        produce<EchainProductStore>(state => {
          // disable no-param-reassign for immer
          /* eslint-disable no-param-reassign */
          state.eChainPartsList?.partsList.forEach(part => {
            // we assume here that cables must be part of an iaConfiguration and there cannot be cables that are not part of an iaConfiguration.
            // so we do not check the origin of the cables
            if (part.associationType === AssociationType.CABLES) {
              const lengthKette = Length.fromJson(get().kette.EffektiveLaenge ?? { Value: 0, Unit: 'mm' });
              const lengthAEs =
                get().eChainPartsList?.partsList.reduce((lastV, newV) => {
                  if (
                    newV.associationType === AssociationType.MOUNTING_BRACKETS &&
                    newV.attributes?.length_mounting_bracket_or_set
                  ) {
                    const elementLength = getAttributeValue<ProductAttributeValueAmountUnit>(
                      newV.attributes
                        ?.length_mounting_bracket_or_set as ProductAttribute<ProductAttributeValueAmountUnit>
                    );
                    if (elementLength?.unit) {
                      // add AE-Length * AE quantity for each AE
                      return Length.fromJson({
                        Unit: 'mm',
                        Value: lastV.getValue(LengthUnits.mm) + elementLength.amount * newV.quantity,
                      });
                    }
                  }
                  return lastV;
                }, Length.mm(0)) ?? Length.mm(0);

              // the function calcLeitungslaenge doesn't work for espools oder rolechains.
              // in this case use: calculateLeitungenLengthForEspool or calculateLeitungenLengthForRolEchain
              // for now we log an error to sentry
              if (state.kette.ArticleType !== ArticleType.kette) {
                Sentry.captureException(new Error(`Trying to calculate cable length on a rol-echain or espool`));
              }

              const length = LeitungCalculator.calcLeitungslaenge(
                Length.mm(lengthAEs.getValue(LengthUnits.mm) + lengthKette.getValue(LengthUnits.mm)),
                Length.fromJson(part.cableUeberstandFestpunkt ?? { Value: 0, Unit: 'm' }),
                Length.fromJson(part.cableUeberstandMitnehmer ?? { Value: 0, Unit: 'm' })
              );

              part.cableLength = length;
              const priceType = getPriceDisplayTypeForOrderType(
                getAttributeValue<string>(part.attributes?.order_type as ProductAttribute<string>)
              );
              const totalPrice = calculateTotalProductPrice(
                priceType,
                part.cableLength.m ?? 0,
                part.quantity,
                getProductBasePrice({
                  prices: part.prices,
                } as ProductFragmentEnhanced),
                getProductLengthPrice({ prices: part.prices } as ProductFragmentEnhanced, priceType) || undefined,
                part.prices
              );
              part.totalPrice = totalPrice.totalCentDiscountPrice ? totalPrice.totalCentDiscountPrice / 100 : -1;
            }
          });
          /* eslint-enable no-param-reassign */
        })
      );
    },
  } as EchainPartsListSlice);
